import { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Upload, Loader2, X, ImagePlus, Music, Youtube, Eye, ChevronLeft, ChevronRight, Video, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, coverSrc, fmtDate, fmtXof } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  SERVICE_GROUPS, SERVICE_FREE_TYPE, DEV_GROUPS, DEV_FREE_TYPE, isExpertiseType,
  DEV_PLATFORMS, DEV_COMPLEXITIES, DEV_DELAYS, DEV_BUDGETS, DEV_SPECS_STATUS,
} from "../../lib/domains";

const EMPTY = {
  title: "", category: "", short_description: "", description: "", tags: "", slug: "",
  cover: "", images: [], presentation_video_url: "", audio_excerpt_path: "",
  price: "", compare_price: "", is_free: false, quote_only: false,
  delivery: "telechargement", file_path: "", file_name: "", video_path: "", external_url: "", stream_url: "", visio_url: "",
  delivery_message: "", status: "brouillon", featured: false, sales_limit_on: false, sales_limit: 10,
  available_from: "", ad_path: "", access_restriction: "tous",
  package_contents: [],
  offer_type: "numerique", milestones: [],
  service_type: "", service_type_other: "", service_duration: "", service_mode: "", service_slots: "", service_places: "",
  service_context: "", service_expert: "",
  dev_type: "", dev_type_other: "", dev_tech: "", dev_delay: "", dev_collab: "", dev_specs_status: "", dev_quote_only: false,
  dev_platform: "", dev_complexity: "", dev_delay_wish: "", dev_budget: "",
};

const slugify = (t) => t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function ProductForm({ open, onClose, product, categories, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("general");
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("general");
    setSlugTouched(false);
    setHistory([]);
    if (product) {
      setForm({
        ...EMPTY,
        title: product.title, category: product.category,
        short_description: product.short_description || "", description: product.description || "",
        tags: (product.tags || []).join(", "), slug: product.slug || slugify(product.title),
        cover: product.cover || "", images: product.images || [],
        presentation_video_url: product.presentation_video_url || "",
        audio_excerpt_path: product.audio_excerpt_path || "",
        price: product.price, compare_price: product.compare_price || "",
        is_free: !!product.is_free, quote_only: !!product.quote_only,
        delivery: product.visio_url ? "visio" : product.external_url ? "lien" : (product.product_type === "streaming" ? "streaming" : "telechargement"),
        file_path: product.file_path || "", file_name: product.file_name || "",
        video_path: product.video_path || "", external_url: product.external_url || "",
        stream_url: product.stream_url || "", visio_url: product.visio_url || "",
        delivery_message: product.delivery_message || "", ad_path: product.ad_path || "",
        package_contents: (product.package_contents || []).map((c, k) => ({ title: c.title || "", type: c.type || "url", url: c.url || "", milestone: c.milestone || "", order: k })),
        offer_type: product.offer_type || "numerique",
        milestones: (product.milestones || []).map((m, k) => ({ key: m.key || `jalon_${k + 1}`, label: m.label || "", amount_mode: m.amount_mode || "pct", value: m.value ?? "", order: k })),
        status: product.status || (product.active ? "publie" : "archive"),
        featured: !!product.featured,
        sales_limit_on: !!product.sales_limit, sales_limit: product.sales_limit || 10,
        available_from: (product.available_from || "").slice(0, 16),
        access_restriction: product.access_restriction || "tous",
        service_type: product.service_type
          ? (SERVICE_GROUPS.some((g) => g.types.includes(product.service_type)) ? product.service_type : SERVICE_FREE_TYPE)
          : "",
        service_type_other: product.service_type && !SERVICE_GROUPS.some((g) => g.types.includes(product.service_type)) ? product.service_type : "",
        service_duration: product.service_duration || "", service_mode: product.service_mode || "",
        service_slots: product.service_slots || "", service_places: product.service_places || "",
        service_context: product.service_context || "", service_expert: product.service_expert || "",
        dev_type: product.dev_type
          ? (DEV_GROUPS.some((g) => g.types.includes(product.dev_type)) ? product.dev_type : DEV_FREE_TYPE)
          : "",
        dev_type_other: product.dev_type && !DEV_GROUPS.some((g) => g.types.includes(product.dev_type)) ? product.dev_type : "",
        dev_tech: product.dev_tech || "",
        dev_delay: product.dev_delay || "", dev_collab: product.dev_collab || "",
        dev_specs_status: product.dev_specs_status || (product.dev_specs_required ? "oui" : ""),
        dev_quote_only: !!product.dev_quote_only,
        dev_platform: product.dev_platform || "", dev_complexity: product.dev_complexity || "",
        dev_delay_wish: product.dev_delay_wish || "", dev_budget: product.dev_budget || "",
      });
      api.get(`/products/${product.product_id}/history`).then(({ data }) => setHistory(data.slice(0, 10))).catch(() => {});
    } else {
      setForm({ ...EMPTY, category: categories[0]?.name || "" });
    }
  }, [open, product, categories]);

  const upload = async (file, kind, field) => {
    if (!file) return;
    if (kind === "cover" && file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)"); return; }
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (field === "images") {
        setForm((f) => ({ ...f, images: [...f.images, data.path].slice(0, 4) }));
      } else {
        setForm((f) => ({ ...f, [field]: data.path, ...(field === "file_path" ? { file_name: data.filename } : {}) }));
      }
      toast.success("Fichier téléversé");
    } catch (e) { toast.error(apiError(e, "Échec du téléversement")); }
    finally { setUploading(""); }
  };

  const dropProps = (kind, field, accept) => ({
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const f = e.dataTransfer.files?.[0];
      if (!f) return;
      if (accept && !f.type.startsWith(accept)) { toast.error("Format de fichier non accepté"); return; }
      upload(f, kind, field);
    },
  });

  const moveImage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.images.length) return;
    const imgs = [...form.images];
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    setForm({ ...form, images: imgs });
  };

  const PKG_TYPES = [
    ["video_youtube", "Vidéo YouTube"],
    ["video_drive", "Vidéo Google Drive"],
    ["pdf", "Document PDF"],
    ["excel", "Fichier Excel"],
    ["url", "Lien externe"],
  ];
  const isPkgUpload = (t) => t === "pdf" || t === "excel";
  const addContent = () => setForm((f) => ({ ...f, package_contents: [...f.package_contents, { title: "", type: "video_youtube", url: "", order: f.package_contents.length }] }));
  const updateContent = (i, key, val) => setForm((f) => { const arr = [...f.package_contents]; arr[i] = { ...arr[i], [key]: val }; return { ...f, package_contents: arr }; });
  const removeContent = (i) => setForm((f) => ({ ...f, package_contents: f.package_contents.filter((_, j) => j !== i).map((c, k) => ({ ...c, order: k })) }));
  const moveContent = (i, dir) => setForm((f) => { const j = i + dir; if (j < 0 || j >= f.package_contents.length) return f; const arr = [...f.package_contents]; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, package_contents: arr.map((c, k) => ({ ...c, order: k })) }; });
  const uploadContent = async (i, file) => {
    if (!file) return;
    setUploading(`pkg-${i}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/package", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => { const arr = [...f.package_contents]; arr[i] = { ...arr[i], url: data.path, title: arr[i].title || data.filename }; return { ...f, package_contents: arr }; });
      toast.success("Fichier téléversé");
    } catch (e) { toast.error(apiError(e, "Échec du téléversement")); }
    finally { setUploading(""); }
  };

  const DEFAULT_MILESTONES = [
    { key: "demarrage", label: "Démarrage (acompte)", amount_mode: "pct", value: 30, order: 0 },
    { key: "intermediaire", label: "Paiement intermédiaire", amount_mode: "pct", value: 40, order: 1 },
    { key: "solde", label: "Solde final", amount_mode: "pct", value: 30, order: 2 },
  ];
  const setOfferType = (v) => setForm((f) => ({
    ...f, offer_type: v,
    milestones: v === "prestation" && f.milestones.length === 0 ? DEFAULT_MILESTONES.map((m) => ({ ...m })) : f.milestones,
  }));
  const addMilestone = () => setForm((f) => ({ ...f, milestones: [...f.milestones, { key: `jalon_${Date.now().toString(36)}`, label: "", amount_mode: "pct", value: "", order: f.milestones.length }] }));
  const updateMilestone = (i, key, val) => setForm((f) => { const arr = [...f.milestones]; arr[i] = { ...arr[i], [key]: val }; return { ...f, milestones: arr }; });
  const removeMilestone = (i) => setForm((f) => { const removed = f.milestones[i]; return { ...f, milestones: f.milestones.filter((_, j) => j !== i).map((m, k) => ({ ...m, order: k })), package_contents: f.package_contents.map((c) => (c.milestone === removed?.key ? { ...c, milestone: "" } : c)) }; });
  const moveMilestone = (i, dir) => setForm((f) => { const j = i + dir; if (j < 0 || j >= f.milestones.length) return f; const arr = [...f.milestones]; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, milestones: arr.map((m, k) => ({ ...m, order: k })) }; });

  const buildPayload = (status) => ({
    title: form.title, category: form.category, short_description: form.short_description,
    description: form.description, slug: form.slug || slugify(form.title),
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    cover: form.cover, images: form.images,
    presentation_video_url: form.presentation_video_url, audio_excerpt_path: form.audio_excerpt_path,
    price: form.is_free ? 0 : parseInt(form.price, 10),
    compare_price: form.compare_price ? parseInt(form.compare_price, 10) : null,
    is_free: form.is_free, quote_only: form.quote_only,
    product_type: form.delivery === "streaming" ? "streaming" : "fichier",
    file_path: form.file_path, file_name: form.file_name, video_path: form.video_path,
    external_url: form.delivery === "lien" ? form.external_url : "",
    stream_url: form.delivery === "streaming" ? form.stream_url : "",
    visio_url: form.delivery === "visio" ? form.visio_url : "",
    delivery_message: form.delivery_message,
    ad_path: form.ad_path, status, featured: form.featured,
    offer_type: form.offer_type,
    milestones: form.offer_type === "prestation"
      ? form.milestones.filter((m) => (m.label || "").trim()).map((m, k) => ({ key: (m.key || `jalon_${k + 1}`).trim(), label: m.label.trim(), amount_mode: m.amount_mode === "fixed" ? "fixed" : "pct", value: parseFloat(m.value) || 0, order: k }))
      : [],
    package_contents: form.package_contents
      .filter((c) => (c.title || "").trim() || (c.url || "").trim())
      .map((c, k) => {
        const validKeys = new Set(form.milestones.filter((m) => (m.label || "").trim()).map((m) => m.key));
        const ms = form.offer_type === "prestation" && validKeys.has(c.milestone) ? c.milestone : "";
        return { title: (c.title || "").trim(), type: c.type, url: (c.url || "").trim(), milestone: ms, order: k };
      }),
    sales_limit: form.sales_limit_on ? parseInt(form.sales_limit, 10) : null,
    available_from: form.available_from || null,
    access_restriction: form.access_restriction,
    service_type: form.service_type === SERVICE_FREE_TYPE ? form.service_type_other : form.service_type,
    service_domain: SERVICE_GROUPS.find((g) => g.types.includes(form.service_type))?.key || "",
    service_duration: form.service_duration, service_mode: form.service_mode,
    service_slots: form.service_slots, service_context: form.service_context, service_expert: form.service_expert,
    service_places: form.service_places ? parseInt(form.service_places, 10) : null,
    dev_type: form.dev_type === DEV_FREE_TYPE ? form.dev_type_other : form.dev_type,
    dev_domain: DEV_GROUPS.find((g) => g.types.includes(form.dev_type))?.key || "",
    dev_tech: form.dev_tech, dev_delay: form.dev_delay, dev_collab: form.dev_collab,
    dev_specs_required: form.dev_specs_status === "oui", dev_specs_status: form.dev_specs_status,
    dev_quote_only: form.dev_quote_only,
    dev_platform: form.dev_platform, dev_complexity: form.dev_complexity,
    dev_delay_wish: form.dev_delay_wish, dev_budget: form.dev_budget,
  });

  const validate = () => {
    if (!form.title || form.title.length < 3) { toast.error("Titre obligatoire (3 caractères min.)"); setTab("general"); return false; }
    if (!form.is_free && !form.quote_only && !(parseInt(form.price) > 0)) { toast.error("Prix obligatoire"); setTab("tarif"); return false; }
    return true;
  };

  const save = async (status, { silent = false } = {}) => {
    if (!validate()) return null;
    setBusy(true);
    try {
      const payload = buildPayload(status);
      let data;
      if (product) ({ data } = await api.patch(`/products/${product.product_id}`, payload));
      else ({ data } = await api.post("/products", payload));
      if (!silent) {
        toast.success(status === "publie" ? "Produit publié" : "Brouillon enregistré");
        onClose();
        onSaved();
      }
      return data;
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible")); return null; }
    finally { setBusy(false); }
  };

  const preview = async () => {
    const status = product ? (product.status || "brouillon") : "brouillon";
    const saved = await save(status, { silent: true });
    if (saved?.product_id) {
      window.open(`/admin/apercu/${saved.product_id}`, "_blank");
      onClose();
      onSaved();
    }
  };

  const discount = form.compare_price && parseInt(form.compare_price) > parseInt(form.price || 0)
    ? Math.round((1 - parseInt(form.price || 0) / parseInt(form.compare_price)) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="catalog-product-form">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{product ? "Modifier le produit" : "Ajouter un produit"}</DialogTitle>
          {product?.updated_at && (
            <p className="text-xs text-slate-400" data-testid="form-last-modified">
              Dernière modification le {fmtDate(product.updated_at)}
            </p>
          )}
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
            <TabsTrigger value="general" data-testid="form-tab-general">Informations générales</TabsTrigger>
            <TabsTrigger value="medias" data-testid="form-tab-medias">Médias</TabsTrigger>
            <TabsTrigger value="tarif" data-testid="form-tab-tarif">Tarification</TabsTrigger>
            <TabsTrigger value="contenu" data-testid="form-tab-contenu">Contenu à livrer</TabsTrigger>
            <TabsTrigger value="params" data-testid="form-tab-params">Publication</TabsTrigger>
          </TabsList>

          {/* ONGLET 1 — INFORMATIONS GÉNÉRALES */}
          <TabsContent value="general" className="mt-5 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="form-offer-type">
              <h3 className="font-mono text-xs uppercase tracking-widest text-slate-500">Type de produit</h3>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button type="button" data-testid="form-offer-numerique" onClick={() => setOfferType("numerique")}
                  className={`rounded-xl border-2 p-3 text-left transition ${form.offer_type === "numerique" ? "border-ciel bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <p className="text-sm font-semibold text-navy">Produit numérique</p>
                  <p className="mt-0.5 text-xs text-slate-500">Vidéos, PDF, Excel, liens — livrés après paiement complet.</p>
                </button>
                <button type="button" data-testid="form-offer-prestation" onClick={() => setOfferType("prestation")}
                  className={`rounded-xl border-2 p-3 text-left transition ${form.offer_type === "prestation" ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <p className="text-sm font-semibold text-navy">Prestation de service</p>
                  <p className="mt-0.5 text-xs text-slate-500">Dév. d'application, expertise, EPP, formation — accès progressif par jalons de paiement.</p>
                </button>
              </div>
            </div>
            <Field label="Titre du produit *">
              <Input data-testid="form-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Catégorie">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Slug URL (généré depuis le titre, modifiable)">
                <Input data-testid="form-slug" value={form.slug}
                  onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }} />
              </Field>
            </div>
            <Field label="Tags / mots-clés SEO (séparés par des virgules)">
              <Input data-testid="form-tags" placeholder="SIH, PMSI, formation…" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            </Field>
            <Field label={`Description courte (${form.short_description.length}/150 — affichée sur la carte produit)`}>
              <Input data-testid="form-short-desc" maxLength={150} value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
            </Field>
            <Field label="Description complète">
              <div data-testid="form-description" className="rounded-md border border-slate-300 [&_.ql-editor]:min-h-[140px]">
                <ReactQuill theme="snow" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              </div>
            </Field>

            {/* PRESTATIONS DE SERVICE */}
            {form.category === "Prestations de service" && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-600">Détails de la prestation</h3>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Type de prestation">
                      <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                        <SelectTrigger data-testid="form-service-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {SERVICE_GROUPS.map((g) => (
                            <SelectGroup key={g.key}>
                              <SelectLabel className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
                              {g.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    {form.service_type === SERVICE_FREE_TYPE && (
                      <Field label="Précisez le type (texte libre)">
                        <Input data-testid="form-service-type-other" value={form.service_type_other} onChange={(e) => setForm({ ...form, service_type_other: e.target.value })} />
                      </Field>
                    )}
                    <Field label="Durée de la prestation">
                      <Input data-testid="form-service-duration" placeholder="Ex : 45 minutes, 2 jours…" value={form.service_duration} onChange={(e) => setForm({ ...form, service_duration: e.target.value })} />
                    </Field>
                  </div>
                  {isExpertiseType(form.service_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Contexte d'intervention">
                        <Input data-testid="form-service-context" placeholder="Ex : Hôpital public, clinique privée, ONG, ministère de la santé, université…" value={form.service_context} onChange={(e) => setForm({ ...form, service_context: e.target.value })} />
                      </Field>
                      <Field label="Nom de l'expert(e) (mention « Dr. … »)">
                        <Input data-testid="form-service-expert" placeholder="Ex : Awa Koné" value={form.service_expert} onChange={(e) => setForm({ ...form, service_expert: e.target.value })} />
                      </Field>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Mode de délivrance">
                      <Select value={form.service_mode} onValueChange={(v) => setForm({ ...form, service_mode: v })}>
                        <SelectTrigger data-testid="form-service-mode"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="visio">Visioconférence (lien envoyé après paiement)</SelectItem>
                          <SelectItem value="presentiel">Présentiel — Abidjan</SelectItem>
                          <SelectItem value="les_deux">Les deux</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Nombre de places disponibles (optionnel)">
                      <Input data-testid="form-service-places" type="number" min={1} value={form.service_places} onChange={(e) => setForm({ ...form, service_places: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Disponibilités (créneaux horaires)">
                    <Input data-testid="form-service-slots" placeholder="Ex : Lun-Ven 9h-17h, sam 9h-12h GMT" value={form.service_slots} onChange={(e) => setForm({ ...form, service_slots: e.target.value })} />
                  </Field>
                </div>
              </div>
            )}

            {/* DÉVELOPPEMENT INFORMATIQUE */}
            {form.category === "Développement informatique" && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-ciel">Détails du développement</h3>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Type de prestation">
                      <Select value={form.dev_type} onValueChange={(v) => setForm({ ...form, dev_type: v })}>
                        <SelectTrigger data-testid="form-dev-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_GROUPS.map((g) => (
                            <SelectGroup key={g.key}>
                              <SelectLabel className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
                              {g.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    {form.dev_type === DEV_FREE_TYPE && (
                      <Field label="Décrivez le besoin (texte libre)">
                        <Input data-testid="form-dev-type-other" value={form.dev_type_other} onChange={(e) => setForm({ ...form, dev_type_other: e.target.value })} />
                      </Field>
                    )}
                    <Field label="Technologie utilisée">
                      <Input data-testid="form-dev-tech" placeholder="Ex : IA Générative, React Native, Python…" value={form.dev_tech} onChange={(e) => setForm({ ...form, dev_tech: e.target.value })} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Plateforme cible">
                      <Select value={form.dev_platform} onValueChange={(v) => setForm({ ...form, dev_platform: v })}>
                        <SelectTrigger data-testid="form-dev-platform"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_PLATFORMS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Niveau de complexité estimé">
                      <Select value={form.dev_complexity} onValueChange={(v) => setForm({ ...form, dev_complexity: v })}>
                        <SelectTrigger data-testid="form-dev-complexity"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_COMPLEXITIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Délai souhaité">
                      <Select value={form.dev_delay_wish} onValueChange={(v) => setForm({ ...form, dev_delay_wish: v })}>
                        <SelectTrigger data-testid="form-dev-delay-wish"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_DELAYS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Budget disponible">
                      <Select value={form.dev_budget} onValueChange={(v) => setForm({ ...form, dev_budget: v })}>
                        <SelectTrigger data-testid="form-dev-budget"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_BUDGETS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Délai de réalisation estimé">
                      <Input data-testid="form-dev-delay" placeholder="Ex : 4 à 6 semaines" value={form.dev_delay} onChange={(e) => setForm({ ...form, dev_delay: e.target.value })} />
                    </Field>
                    <Field label="Mode de collaboration">
                      <Select value={form.dev_collab} onValueChange={(v) => setForm({ ...form, dev_collab: v })}>
                        <SelectTrigger data-testid="form-dev-collab"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en_ligne">100 % en ligne</SelectItem>
                          <SelectItem value="presentiel">Présentiel — Abidjan</SelectItem>
                          <SelectItem value="hybride">Hybride</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <Field label="Cahier des charges disponible">
                      <Select value={form.dev_specs_status} onValueChange={(v) => setForm({ ...form, dev_specs_status: v })}>
                        <SelectTrigger data-testid="form-dev-specs" className="w-64"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          {DEV_SPECS_STATUS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <label className="mt-5 flex items-center gap-2 text-sm text-slate-600">
                      <Switch checked={form.dev_quote_only} onCheckedChange={(v) => setForm({ ...form, dev_quote_only: v })} data-testid="form-dev-quote" />
                      Devis sur demande (le prix devient « Nous contacter pour un devis »)
                    </label>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ONGLET 2 — MÉDIAS */}
          <TabsContent value="medias" className="mt-5 space-y-4">
            <Field label="Image de couverture principale (JPG/PNG, max 5 Mo) — cliquer ou glisser-déposer">
              <div className="flex items-center gap-3">
                {form.cover && <img src={coverSrc(form.cover)} alt="" className="h-14 w-20 rounded object-cover" data-testid="form-cover-preview" />}
                <label {...dropProps("cover", "cover", "image/")}
                  className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 hover:border-ciel" data-testid="form-cover-upload">
                  <Upload size={14} /> {uploading === "cover" ? "Téléversement…" : form.cover ? "Remplacer l'image" : "Téléverser ou déposer une image"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "cover", "cover")} />
                </label>
              </div>
            </Field>
            <Field label={`Images secondaires (${form.images.length}/4) — réorganisables avec les flèches`}>
              <div className="flex flex-wrap items-center gap-2">
                {form.images.map((img, i) => (
                  <span key={i} className="relative" data-testid={`form-image-${i}`}>
                    <img src={coverSrc(img)} alt="" className="h-14 w-20 rounded border border-slate-200 object-cover" />
                    <span className="absolute -top-1.5 left-1 flex gap-0.5">
                      <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0}
                        className="flex h-4 w-4 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-image-left-${i}`}>
                        <ChevronLeft size={10} />
                      </button>
                      <button type="button" onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1}
                        className="flex h-4 w-4 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-image-right-${i}`}>
                        <ChevronRight size={10} />
                      </button>
                    </span>
                    <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                      className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white" data-testid={`form-image-remove-${i}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
                {form.images.length < 4 && (
                  <label {...dropProps("extra", "images", "image/")}
                    className="flex h-14 w-20 cursor-pointer items-center justify-center rounded border border-dashed border-slate-300 text-slate-400 hover:border-ciel" data-testid="form-image-add">
                    {uploading === "images" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={16} />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "extra", "images")} />
                  </label>
                )}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vidéo de présentation (URL YouTube/Vimeo)">
                <Input data-testid="form-video-url" placeholder="https://youtube.com/watch?v=…" value={form.presentation_video_url}
                  onChange={(e) => setForm({ ...form, presentation_video_url: e.target.value })} />
              </Field>
              <Field label="Ou vidéo de présentation MP4 (glisser-déposer)">
                <label {...dropProps("video", "presentation_video_url", "video/")}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-2.5 text-xs text-slate-500 hover:border-ciel" data-testid="form-presentation-upload">
                  <Video size={14} /> {uploading === "presentation_video_url" ? "Téléversement…" : form.presentation_video_url && !form.presentation_video_url.startsWith("http") ? "Vidéo téléversée ✓" : "Téléverser un MP4"}
                  <input type="file" accept="video/mp4" className="hidden" onChange={(e) => upload(e.target.files[0], "video", "presentation_video_url")} />
                </label>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Extrait audio (MP3, max 60 s)">
                <label {...dropProps("extra", "audio_excerpt_path", "audio/")}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-2.5 text-xs text-slate-500 hover:border-ciel" data-testid="form-audio-upload">
                  <Music size={14} /> {uploading === "audio_excerpt_path" ? "Téléversement…" : form.audio_excerpt_path ? "Extrait téléversé ✓" : "Téléverser un MP3"}
                  <input type="file" accept="audio/mpeg" className="hidden" onChange={(e) => upload(e.target.files[0], "extra", "audio_excerpt_path")} />
                </label>
              </Field>
              <Field label="Vidéo publicitaire (bouton « Voir la publicité »)">
                <label {...dropProps("ad", "ad_path", "video/")}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-2.5 text-xs text-slate-500 hover:border-amber-500" data-testid="form-ad-upload">
                  <Youtube size={14} /> {uploading === "ad_path" ? "Téléversement…" : form.ad_path ? "Publicité téléversée ✓" : "Téléverser la vidéo publicitaire (MP4)"}
                  <input type="file" accept="video/mp4" className="hidden" onChange={(e) => upload(e.target.files[0], "ad", "ad_path")} />
                </label>
              </Field>
            </div>
          </TabsContent>

          {/* ONGLET 3 — TARIFICATION */}
          <TabsContent value="tarif" className="mt-5 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Prix en FCFA *">
                <Input data-testid="form-price" type="number" min={0} disabled={form.is_free || form.quote_only} value={form.is_free ? 0 : form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </Field>
              <Field label="Prix barré (promo)">
                <Input data-testid="form-compare-price" type="number" min={0} disabled={form.is_free || form.quote_only} value={form.compare_price}
                  onChange={(e) => setForm({ ...form, compare_price: e.target.value })} />
              </Field>
              <Field label="Réduction calculée">
                <div className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-sm font-semibold text-emerald-600" data-testid="form-discount">
                  {discount > 0 ? `-${discount}%` : "—"}
                </div>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v, ...(v ? { quote_only: false } : {}) })} data-testid="form-free" />
              Produit gratuit (le prix devient « Gratuit », accès immédiat sans paiement)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={form.quote_only} onCheckedChange={(v) => setForm({ ...form, quote_only: v, ...(v ? { is_free: false } : {}) })} data-testid="form-quote-only" />
              Devis sur demande (masque le prix et affiche « Nous contacter »)
            </label>
            <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
              Devise d'affichage par défaut : <strong>FCFA</strong>. La conversion se fait automatiquement selon le sélecteur de devises du client (11 devises, taux réels).
            </p>
          </TabsContent>

          {/* ONGLET 4 — CONTENU À LIVRER */}
          <TabsContent value="contenu" className="mt-5 space-y-4">
            <Field label="Type de livraison">
              <Select value={form.delivery} onValueChange={(v) => setForm({ ...form, delivery: v })}>
                <SelectTrigger data-testid="form-delivery"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="telechargement">Téléchargement direct (fichier PDF/ZIP/MP4)</SelectItem>
                  <SelectItem value="lien">Lien externe sécurisé (URL privée)</SelectItem>
                  <SelectItem value="streaming">Accès streaming (URL vidéo privée)</SelectItem>
                  <SelectItem value="visio">Lien de visioconférence (envoyé après paiement)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.delivery === "telechargement" && (
              <Field label="Fichier du contenu (cliquer ou glisser-déposer)">
                <label {...dropProps("file", "file_path")}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 hover:border-ciel" data-testid="form-file-upload">
                  <Upload size={14} /> {uploading === "file_path" ? "Téléversement…" : form.file_path ? `Fichier : ${form.file_name}` : "Téléverser le fichier"}
                  <input type="file" className="hidden" onChange={(e) => upload(e.target.files[0], "file", "file_path")} />
                </label>
              </Field>
            )}
            {form.delivery === "lien" && (
              <Field label="URL du contenu hébergé">
                <Input data-testid="form-external-url" placeholder="https://…" value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })} />
              </Field>
            )}
            {form.delivery === "visio" && (
              <Field label="Lien de visioconférence (Zoom, Meet, Teams…) — communiqué au client après paiement">
                <Input data-testid="form-visio-url" placeholder="https://meet.google.com/…" value={form.visio_url}
                  onChange={(e) => setForm({ ...form, visio_url: e.target.value })} />
              </Field>
            )}
            {form.delivery === "streaming" && (
              <div className="space-y-3">
                <Field label="Vidéo MP4 téléversée (streaming dans l'application)">
                  <label {...dropProps("video", "video_path", "video/")}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 hover:border-ciel" data-testid="form-video-upload">
                    <Upload size={14} /> {uploading === "video_path" ? "Téléversement…" : form.video_path ? "Vidéo téléversée ✓" : "Téléverser la vidéo MP4"}
                    <input type="file" accept="video/mp4" className="hidden" onChange={(e) => upload(e.target.files[0], "video", "video_path")} />
                  </label>
                </Field>
                <Field label="OU URL de vidéo privée (YouTube non répertoriée, Vimeo…)">
                  <Input data-testid="form-stream-url" placeholder="https://…" value={form.stream_url}
                    onChange={(e) => setForm({ ...form, stream_url: e.target.value })} />
                </Field>
              </div>
            )}
            {form.offer_type === "prestation" && (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50/50 p-4" data-testid="form-milestones">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-700">Jalons de paiement (prestation)</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addMilestone} data-testid="form-milestone-add" className="rounded-full text-xs">
                    <Plus size={13} className="mr-1" /> Ajouter un jalon
                  </Button>
                </div>
                <p className="mt-1 text-xs text-slate-500">Définissez les jalons et leurs montants (en % du prix total ou en montant fixe FCFA). Le vendeur valide chaque jalon dans l'admin après réception du paiement, ce qui débloque les contenus rattachés.</p>
                <ul className="mt-3 space-y-2">
                  {form.milestones.map((m, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`form-milestone-item-${i}`}>
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">{i + 1}</span>
                      <Input className="min-w-[160px] flex-1" placeholder="Libellé du jalon (ex : Démarrage)" value={m.label}
                        onChange={(e) => updateMilestone(i, "label", e.target.value)} data-testid={`form-milestone-label-${i}`} />
                      <Select value={m.amount_mode} onValueChange={(v) => updateMilestone(i, "amount_mode", v)}>
                        <SelectTrigger className="w-32" data-testid={`form-milestone-mode-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pct">% du total</SelectItem>
                          <SelectItem value="fixed">Montant FCFA</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input className="w-28" type="number" min={0} placeholder={m.amount_mode === "fixed" ? "FCFA" : "%"} value={m.value}
                        onChange={(e) => updateMilestone(i, "value", e.target.value)} data-testid={`form-milestone-value-${i}`} />
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveMilestone(i, -1)} disabled={i === 0} className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-milestone-up-${i}`}><ChevronLeft className="rotate-90" size={12} /></button>
                        <button type="button" onClick={() => moveMilestone(i, 1)} disabled={i === form.milestones.length - 1} className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-milestone-down-${i}`}><ChevronRight className="rotate-90" size={12} /></button>
                        <button type="button" onClick={() => removeMilestone(i)} className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white" data-testid={`form-milestone-remove-${i}`}><X size={12} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
                {(() => {
                  const labeled = form.milestones.filter((m) => (m.label || "").trim());
                  if (labeled.length === 0) return null;
                  const price = parseFloat(form.price) || 0;
                  const hasFixed = labeled.some((m) => m.amount_mode === "fixed");
                  const warnCls = "mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800";
                  const okCls = "mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700";
                  if (!hasFixed) {
                    const pctSum = labeled.reduce((s, m) => s + (parseFloat(m.value) || 0), 0);
                    return Math.round(pctSum) !== 100
                      ? <p className={warnCls} data-testid="milestone-sum-warning">⚠️ La somme des pourcentages des jalons est {pctSum} % — elle devrait totaliser 100 %.</p>
                      : <p className={okCls} data-testid="milestone-sum-ok">Somme des jalons : 100 % ✓</p>;
                  }
                  if (price > 0) {
                    const total = labeled.reduce((s, m) => s + (m.amount_mode === "fixed" ? (parseFloat(m.value) || 0) : Math.round(price * (parseFloat(m.value) || 0) / 100)), 0);
                    return total !== price
                      ? <p className={warnCls} data-testid="milestone-sum-warning">⚠️ Le total des jalons ({fmtXof(total)}) ne correspond pas au prix du produit ({fmtXof(price)}).</p>
                      : <p className={okCls} data-testid="milestone-sum-ok">Total des jalons = prix du produit ✓</p>;
                  }
                  return <p className="mt-3 text-xs text-slate-400" data-testid="milestone-sum-info">Renseignez le prix du produit pour vérifier le total des jalons.</p>;
                })()}
              </div>
            )}

            <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="form-package">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-mono text-xs uppercase tracking-widest text-indigo-600">{form.offer_type === "prestation" ? "Livrables de la prestation" : "Contenus du package (livrés après paiement)"}</h3>
                <Button type="button" variant="outline" size="sm" onClick={addContent} data-testid="form-package-add" className="rounded-full text-xs">
                  <Plus size={13} className="mr-1" /> Ajouter un contenu
                </Button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Composez un package multi-contenus : vidéos (YouTube / Google Drive), PDF, Excel ou liens. Ils sont envoyés par email et disponibles dans « Mes commandes » du client après achat.</p>
              {form.package_contents.length === 0 ? (
                <p className="mt-3 text-xs text-slate-400" data-testid="form-package-empty">Aucun contenu ajouté. Optionnel — utilisez-le pour livrer plusieurs ressources (ex : 6 vidéos de formation).</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {form.package_contents.map((c, i) => (
                    <li key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`form-package-item-${i}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={c.type} onValueChange={(v) => updateContent(i, "type", v)}>
                          <SelectTrigger className="w-44" data-testid={`form-package-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{PKG_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="min-w-[160px] flex-1" placeholder="Titre du contenu (ex : Module 1 — Introduction)" value={c.title}
                          onChange={(e) => updateContent(i, "title", e.target.value)} data-testid={`form-package-title-${i}`} />
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveContent(i, -1)} disabled={i === 0}
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-package-up-${i}`}><ChevronLeft className="rotate-90" size={12} /></button>
                          <button type="button" onClick={() => moveContent(i, 1)} disabled={i === form.package_contents.length - 1}
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-white disabled:opacity-30" data-testid={`form-package-down-${i}`}><ChevronRight className="rotate-90" size={12} /></button>
                          <button type="button" onClick={() => removeContent(i)}
                            className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white" data-testid={`form-package-remove-${i}`}><X size={12} /></button>
                        </div>
                      </div>
                      <div className="mt-2">
                        {isPkgUpload(c.type) ? (
                          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-2.5 text-xs text-slate-500 hover:border-ciel" data-testid={`form-package-upload-${i}`}>
                            <Upload size={14} /> {uploading === `pkg-${i}` ? "Téléversement…" : c.url ? "Fichier téléversé ✓ (cliquer pour remplacer)" : `Téléverser le ${c.type === "pdf" ? "PDF" : "fichier Excel"}`}
                            <input type="file" accept={c.type === "pdf" ? ".pdf" : ".xlsx,.xls,.csv"} className="hidden" onChange={(e) => uploadContent(i, e.target.files[0])} />
                          </label>
                        ) : (
                          <Input placeholder={c.type === "video_youtube" ? "https://youtube.com/watch?v=…" : c.type === "video_drive" ? "https://drive.google.com/file/d/…" : "https://…"}
                            value={c.url} onChange={(e) => updateContent(i, "url", e.target.value)} data-testid={`form-package-url-${i}`} />
                        )}
                      </div>
                      {form.offer_type === "prestation" && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Débloqué au jalon :</span>
                          <Select value={c.milestone || "__none__"} onValueChange={(v) => updateContent(i, "milestone", v === "__none__" ? "" : v)}>
                            <SelectTrigger className="w-64" data-testid={`form-package-milestone-${i}`}><SelectValue placeholder="Choisir un jalon…" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Accessible dès la commande</SelectItem>
                              {form.milestones.filter((m) => (m.label || "").trim()).map((m, k) => (
                                <SelectItem key={m.key || k} value={m.key || `jalon_${k + 1}`}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Field label="Message de livraison personnalisé (envoyé au client après achat)">
              <textarea data-testid="form-delivery-message" rows={3} value={form.delivery_message}
                placeholder={"Bonjour [Prénom], voici votre accès à [Titre du produit] : [Lien / Fichier]. Merci pour votre confiance — Cabinet CARIS Santé."}
                onChange={(e) => setForm({ ...form, delivery_message: e.target.value })}
                className="w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
            </Field>
            <Field label="Aperçu gratuit (extrait texte visible par tous)">
              <Input data-testid="form-preview" value={form.preview || ""} onChange={(e) => setForm({ ...form, preview: e.target.value })} />
            </Field>
          </TabsContent>

          {/* ONGLET 5 — PARAMÈTRES DE PUBLICATION */}
          <TabsContent value="params" className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Statut">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">Brouillon (non visible au public)</SelectItem>
                    <SelectItem value="publie">Publié (visible en boutique immédiatement)</SelectItem>
                    <SelectItem value="archive">Archivé (retiré de la boutique)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Publication programmée (date et heure — optionnel)">
                <Input data-testid="form-available-from" type="datetime-local" value={form.available_from}
                  onChange={(e) => setForm({ ...form, available_from: e.target.value })} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
              Produit mis en avant (affiché en priorité sur la page d'accueil)
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={form.sales_limit_on} onCheckedChange={(v) => setForm({ ...form, sales_limit_on: v })} data-testid="form-limit-switch" />
                Limiter les ventes
              </label>
              {form.sales_limit_on && (
                <Input data-testid="form-limit" type="number" min={1} value={form.sales_limit} className="w-36"
                  placeholder="Nb max d'acheteurs"
                  onChange={(e) => setForm({ ...form, sales_limit: e.target.value })} />
              )}
            </div>
            <Field label="Restriction d'accès">
              <Select value={form.access_restriction} onValueChange={(v) => setForm({ ...form, access_restriction: v })}>
                <SelectTrigger data-testid="form-access-restriction" className="w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tout le monde</SelectItem>
                  <SelectItem value="inscrits">Clients enregistrés uniquement</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </TabsContent>
        </Tabs>

        {/* HISTORIQUE DES 10 DERNIÈRES MODIFICATIONS */}
        {product && history.length > 0 && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="form-history">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Historique — 10 dernières modifications</p>
            <ul className="mt-2 max-h-36 space-y-1.5 overflow-y-auto">
              {history.map((h, i) => (
                <li key={i} className="text-xs text-slate-500">
                  <span className="font-mono text-slate-400">{fmtDate(h.at)}</span> · {h.by} ·{" "}
                  {h.changes.map((ch) => ch.field).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* BOUTONS */}
        <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-4">
          <Button data-testid="form-preview-btn" variant="outline" onClick={preview} disabled={busy}
            className="rounded-full py-5 text-ciel hover:text-sky-700">
            <Eye size={15} className="mr-1.5" /> Aperçu public
          </Button>
          <Button data-testid="form-save-draft" variant="outline" onClick={() => save("brouillon")} disabled={busy}
            className="flex-1 rounded-full py-5">
            Enregistrer en brouillon
          </Button>
          <Button data-testid="form-publish" onClick={() => save("publie")} disabled={busy}
            className="flex-1 rounded-full bg-navy py-5 text-sm font-semibold text-white hover:bg-navy-light">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Publier maintenant"}
          </Button>
          <Button data-testid="form-cancel" variant="ghost" onClick={onClose} disabled={busy} className="rounded-full py-5 text-slate-500">
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
